import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSpreadsheet, FileText, FileDown, Loader2, Lock, Package } from "lucide-react";
import { api, downloadFile, fmtDate } from "@/lib/api";

const FORMATS = [
  { format: "zip", label: "Complete toolkit (.zip)", desc: "Everything in one download: the full functional Excel workbook (with dashboard, risk register, risk matrix, audit register, trackers, calendar, 30/60/90 plan and more), the SOP template pack, and the Quick-Start PDF guide.", icon: Package, tid: "toolkit-download-zip" },
  { format: "xlsx", label: "Excel workbook", desc: "The integrated functional workbook — dashboard, risk register with automatic scoring and the 5×5 matrix, audit register, SOP inventory, license & insurance tracker, contract inventory, vendor register, incident log, compliance calendar, 30/60/90 plan, RACI, org chart, continuity plan, and the annual review checklist.", icon: FileSpreadsheet, tid: "toolkit-download-xlsx" },
  { format: "docx", label: "SOP template pack", desc: "All 13 SOP starters as editable Word-compatible templates with full section prompts.", icon: FileText, tid: "toolkit-download-docx" },
  { format: "pdf", label: "Quick-start guide", desc: "A ten-step guide to getting your system in place, plus the annual review checklist for your yearly planning meeting.", icon: FileDown, tid: "toolkit-download-pdf" },
];

export default function Toolkit() {
  const [access, setAccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/api/app/toolkit/access").then((r) => setAccess(r.data)).catch(() => setError("Could not load toolkit access."));
  }, []);

  if (!access) {
    return (
      <div className="flex h-64 items-center justify-center">
        {error ? <p className="text-sm text-red-600">{error}</p> : <Loader2 className="h-8 w-8 animate-spin text-gold" />}
      </div>
    );
  }

  if (!access.has_access) {
    return (
      <div className="mx-auto max-w-2xl" data-testid="toolkit-locked">
        <p className="eyebrow">MITIVISE Toolkit</p>
        <div className="mt-6 rounded-lg border border-navy/10 bg-white p-10 text-center">
          <Package className="mx-auto h-12 w-12 text-navy/30" />
          <h1 className="mt-5 font-serif text-3xl font-semibold text-navy">Toolkit access isn't active</h1>
          <p className="mt-3 text-sm leading-relaxed text-ink/65">
            The downloadable toolkit comes with a one-time toolkit purchase or an active MITIVISE Workspace subscription.
          </p>
          <Link to="/app/billing" data-testid="toolkit-locked-cta" className="btn-gold mt-7">See purchase options</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl" data-testid="toolkit-page">
      <p className="eyebrow">MITIVISE Toolkit</p>
      <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Your downloadable toolkit</h1>
      <p className="mt-3 max-w-2xl text-sm text-ink/60">
        Generate and download your files as often as you like. Access runs through{" "}
        <span className="font-semibold text-navy">{fmtDate(access.access_until)}</span>
        {access.subscription_active ? " (active while your MITIVISE Workspace subscription continues)" : ` (${access.access_days} days from purchase)`}
        {" "}— and the files you download are yours to keep forever.
      </p>

      <div className="mt-8 space-y-4">
        {FORMATS.map((f) => (
          <div key={f.format} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-navy/10 bg-white p-6" data-testid={`toolkit-card-${f.format}`}>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-navy text-gold">
                <f.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-serif text-xl font-semibold text-navy">{f.label}</p>
                <p className="mt-1 max-w-md text-sm text-ink/60">{f.desc}</p>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await downloadFile(`/api/app/toolkit/download?format=${f.format}`, `TSM_Compliance_OS_Toolkit.${f.format}`);
                } catch (e) {
                  setError("Download failed — please try again.");
                }
              }}
              data-testid={f.tid}
              className="btn-gold !py-2.5"
            >
              <FileDown className="h-4 w-4" />
              Download
            </button>
          </div>
        ))}
      </div>

      {error && <p className="mt-5 text-sm text-red-600" data-testid="toolkit-error">{error}</p>}
      <p className="mt-8 flex items-start gap-2 rounded-sm border border-gold/40 bg-gold/5 p-4 text-xs italic text-navy/70">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-golddark" />
        The toolkit is a static product — it does not include cloud monitoring, AI generation, or ongoing compliance tracking.
        Those are features of MITIVISE Workspace.
      </p>
    </div>
  );
}
